import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Button,
  Badge,
  Skeleton,
  Alert,
  Modal,
  Pagination,
  EmptyState,
  VariantMatrix,
} from '@bbpos/ui';
import type { Product } from '@bbpos/types';
import { productsService, type StockHistoryEntry } from '@/services/products.service';
import { useCartStore } from '@/stores/use-cart-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { ApiError } from '@/lib/api-client';
import { formatCurrency, formatDateTime, formatPriceRange } from '@/lib/format';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { getStockBadge, isManagerOrAbove, isOutOfStock } from '@/lib/product-helpers';
import { mapVariantsResponse, type PickerVariants } from '@/lib/variant-selection';
import { ChevronLeftIcon, PackageIcon, ShoppingCartIcon, ClockIcon } from '@/lib/icons';
import { ProductLocationsPanel } from '@/components/features/products/ProductLocationsPanel';

// ─── Helpers ───────────────────────────────────────────────────────────────

function isExpiryApproaching(expiryDate: string): boolean {
  const expiry = new Date(expiryDate);
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 30;
}

function isExpired(expiryDate: string): boolean {
  return new Date(expiryDate) < new Date();
}

// ─── Component ─────────────────────────────────────────────────────────────

const STOCK_HISTORY_PAGE_SIZE = 10;

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const addItem = useCartStore((s) => s.addItem);
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const currency = useTenantCurrency();
  const tax = useTaxRegime();

  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // A variant parent holds no stock of its own — its children do (PS3-001).
  const [variantPicker, setVariantPicker] = useState<PickerVariants | null>(null);
  const [showVariantPicker, setShowVariantPicker] = useState(false);

  // Stock history modal
  const [showStockHistory, setShowStockHistory] = useState(false);
  const [stockHistory, setStockHistory] = useState<StockHistoryEntry[]>([]);
  const [stockHistoryMeta, setStockHistoryMeta] = useState({ total: 0, page: 1, limit: STOCK_HISTORY_PAGE_SIZE, totalPages: 0 });
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [stockHistoryPage, setStockHistoryPage] = useState(1);

  // ── Fetch product ─────────────────────────────────────────────────────
  const fetchProduct = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await productsService.getById(id);
      const picker = data.is_variant || data.parent_product_id
        ? null
        : await productsService.getVariants(id, 'pos').then(mapVariantsResponse, () => null);
      setProduct(data);
      setVariantPicker(picker && picker.variants.length > 0 ? picker : null);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load product details.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  // ── Fetch stock history ───────────────────────────────────────────────
  useEffect(() => {
    if (!showStockHistory || !id) return;
    let cancelled = false;
    async function fetchHistory() {
      setIsLoadingHistory(true);
      try {
        const result = await productsService.getStockHistory(id!, stockHistoryPage, STOCK_HISTORY_PAGE_SIZE);
        if (cancelled) return;
        setStockHistory(result.data);
        setStockHistoryMeta(result.meta);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load stock history.');
        }
      } finally {
        if (!cancelled) setIsLoadingHistory(false);
      }
    }
    fetchHistory();
    return () => { cancelled = true; };
  }, [showStockHistory, id, stockHistoryPage]);

  // ── Add to cart ───────────────────────────────────────────────────────
  const handleAddToCart = useCallback(() => {
    if (!product) return;
    if (variantPicker) {
      setShowVariantPicker(true);
      return;
    }
    addItem(product);
    navigate('/pos/billing');
  }, [product, variantPicker, addItem, navigate]);

  // ── Render ────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
        <header className="shrink-0 flex items-center gap-4 px-6 h-14 bg-white dark:bg-neutral-800 shadow-xs">
          <Skeleton variant="text" width={200} height={20} />
        </header>
        <div className="p-6 space-y-4">
          <Skeleton variant="rect" width="100%" height={200} />
          <Skeleton variant="rect" width="100%" height={120} />
          <Skeleton variant="rect" width="100%" height={80} />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
        <header className="shrink-0 flex items-center gap-4 px-6 h-14 bg-white dark:bg-neutral-800 shadow-xs">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pos/products')}>
            <ChevronLeftIcon />
            <span className="ml-1">Products</span>
          </Button>
        </header>
        <EmptyState
          variant="page"
          title="Product not found"
          description={error ?? 'The requested product could not be loaded.'}
          action={{ label: 'Back to Catalog', onClick: () => navigate('/pos/products') }}
        />
      </div>
    );
  }

  const canViewCost = isManagerOrAbove(user?.role);
  const locationName = profile?.locations?.find((l) => l.id === product.location_id)?.name;
  const variantRows = variantPicker ? [...variantPicker.children.values()] : [];
  // Stock and out-of-stock read the variants' sum for a parent, its own row otherwise.
  const stockView = variantPicker
    ? {
        ...product,
        stock_quantity:
          Math.round(variantRows.reduce((sum, v) => sum + Number(v.stock_quantity), 0) * 1000) / 1000,
      }
    : product;
  // A parent sells at its variants' prices, not its own (the wizard saves 0).
  const variantPrices = variantRows.map((v) => Number(v.price));

  return (
    <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
      {/* ═══ Header ═══ */}
      <header className="shrink-0 flex items-center gap-4 px-6 h-14 bg-white dark:bg-neutral-800 shadow-xs dark:shadow-none">
        <Button variant="ghost" size="sm" onClick={() => navigate('/pos/products')}>
          <ChevronLeftIcon />
          <span className="ml-1 hidden sm:inline">Products</span>
        </Button>
        <div className="flex items-center gap-3 min-w-0">
          <h1 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 truncate">
            {product.name}
          </h1>
          {product.is_active ? (
            <Badge variant="success" size="sm" dot>Active</Badge>
          ) : (
            <Badge variant="draft" size="sm" dot>Inactive</Badge>
          )}
        </div>
      </header>

      {/* ═══ Banners ═══ */}
      {error && (
        <div className="px-6 pt-3 shrink-0">
          <Alert variant="danger" description={error} dismissible onDismiss={() => setError(null)} />
        </div>
      )}
      {successMsg && (
        <div className="px-6 pt-3 shrink-0">
          <Alert variant="success" description={successMsg} dismissible onDismiss={() => setSuccessMsg(null)} />
        </div>
      )}

      {/* ═══ Content ═══ */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">

        {/* ── Product Info ───────────────────────────────────────────── */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Image */}
            <div className="shrink-0 flex items-start justify-center">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="h-48 w-48 rounded-lg object-cover"
                />
              ) : (
                <div className="h-48 w-48 rounded-lg bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center">
                  <PackageIcon className="h-16 w-16 text-neutral-300 dark:text-neutral-600" />
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                  {product.name}
                </h2>
                {(product.is_variant || product.parent_product_id) && (
                  <Badge variant="info" size="sm">Variant</Badge>
                )}
              </div>

              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">SKU</dt>
                  <dd className="font-mono text-neutral-900 dark:text-neutral-100">{product.sku}</dd>
                </div>
                {product.barcode && (
                  <div>
                    <dt className="text-neutral-500 dark:text-neutral-400">Barcode</dt>
                    <dd className="font-mono text-neutral-900 dark:text-neutral-100">{product.barcode}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">Category</dt>
                  <dd className="text-neutral-900 dark:text-neutral-100">{product.category || '—'}</dd>
                </div>
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">Unit</dt>
                  <dd className="text-neutral-900 dark:text-neutral-100">{product.unit}</dd>
                </div>
                {locationName && (
                  <div>
                    <dt className="text-neutral-500 dark:text-neutral-400">Location</dt>
                    <dd className="text-neutral-900 dark:text-neutral-100">{locationName}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">Selling Price</dt>
                  <dd className="font-semibold text-neutral-900 dark:text-neutral-100">
                    {variantPrices.length > 0
                      ? formatPriceRange(Math.min(...variantPrices), Math.max(...variantPrices), currency)
                      : formatCurrency(product.price, currency)}
                  </dd>
                </div>
                {canViewCost && (
                  <div>
                    <dt className="text-neutral-500 dark:text-neutral-400">Cost (WAC)</dt>
                    <dd className="text-neutral-900 dark:text-neutral-100">
                      {formatCurrency(product.cost, currency)}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-neutral-500 dark:text-neutral-400">{tax.taxName} Rate</dt>
                  <dd>
                    <Badge variant="info" size="sm">{product.gst_rate}%</Badge>
                  </dd>
                </div>
                {/* HSN/SAC is an India-only statutory code — hidden for every GCC regime. */}
                {tax.showHsn && product.hsn_code && (
                  <div>
                    <dt className="text-neutral-500 dark:text-neutral-400">HSN Code</dt>
                    <dd className="font-mono text-neutral-900 dark:text-neutral-100">{product.hsn_code}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>

        {/* ── Stock Info ──────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
          <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
            Stock Information
          </h2>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
                {stockView.stock_quantity}
              </div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Current Stock
              </div>
            </div>
            <div className="text-center">
              <div className="text-lg font-medium text-neutral-700 dark:text-neutral-300">
                {product.low_stock_threshold}
              </div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Low Stock Threshold
              </div>
            </div>
            <div className="ml-auto">
              {getStockBadge(stockView)}
            </div>
          </div>
        </div>

        {/* ── Expiry Date ─────────────────────────────────────────────── */}
        {product.expiry_date && (
          <div className={`rounded-lg border p-4 ${
            isExpired(product.expiry_date)
              ? 'bg-danger-50 dark:bg-danger-950 border-danger-300 dark:border-danger-700'
              : isExpiryApproaching(product.expiry_date)
                ? 'bg-warning-50 dark:bg-warning-950 border-warning-300 dark:border-warning-700'
                : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700'
          }`}>
            <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
              Expiry Date
            </h2>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${
                isExpired(product.expiry_date)
                  ? 'text-danger-700 dark:text-danger-300'
                  : isExpiryApproaching(product.expiry_date)
                    ? 'text-warning-700 dark:text-warning-300'
                    : 'text-neutral-900 dark:text-neutral-100'
              }`}>
                {formatDateTime(product.expiry_date)}
              </span>
              {isExpired(product.expiry_date) && (
                <Badge variant="danger" size="sm">Expired</Badge>
              )}
              {!isExpired(product.expiry_date) && isExpiryApproaching(product.expiry_date) && (
                <Badge variant="warning" size="sm">Expiring Soon</Badge>
              )}
            </div>
          </div>
        )}

        {/* ── Variant Info ────────────────────────────────────────────── */}
        {(product.is_variant || product.variant_attributes) && (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
              Variant Information
            </h2>
            {product.variant_attributes && Object.keys(product.variant_attributes).length > 0 && (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-3">
                {Object.entries(product.variant_attributes).map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-neutral-500 dark:text-neutral-400 capitalize">{key}</dt>
                    <dd className="text-neutral-900 dark:text-neutral-100">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {product.parent_product_id && (
              <Link
                to={`/pos/products/${product.parent_product_id}`}
                className="text-sm text-brand-600 dark:text-brand-400 hover:underline"
              >
                View Parent Product
              </Link>
            )}
          </div>
        )}

        {/* ── Variants of a parent ────────────────────────────────────── */}
        {variantRows.length > 0 && (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
              Variants
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider border-b border-neutral-200 dark:border-neutral-700">
                    <th className="px-3 py-2">Variant</th>
                    <th className="px-3 py-2">SKU</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Stock</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                  {variantRows.map((v) => (
                    <tr
                      key={v.id}
                      className="even:bg-neutral-50 dark:even:bg-neutral-900 hover:bg-brand-50 dark:hover:bg-neutral-700 transition-all duration-200"
                    >
                      <td className="px-3 py-2 text-neutral-900 dark:text-neutral-100">
                        <Link to={`/pos/products/${v.id}`} className="hover:underline">
                          {Object.values(v.variant_attributes ?? {}).map(String).join(' / ') || v.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 font-mono text-neutral-600 dark:text-neutral-400">{v.sku}</td>
                      <td className="px-3 py-2 text-right text-neutral-900 dark:text-neutral-100 tabular-nums">
                        {formatCurrency(v.price, currency)}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-neutral-900 dark:text-neutral-100 tabular-nums">
                        {Number(v.stock_quantity)}
                      </td>
                      <td className="px-3 py-2 text-right">{getStockBadge(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Locations (master catalogue, Manager and up) ───────────── */}
        {canViewCost && (
          <ProductLocationsPanel
            productId={product.id}
            menu={profile?.tenant?.business_type === 'RESTAURANT'}
          />
        )}

        {/* ── Timestamps ──────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
          <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
            Record Info
          </h2>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-neutral-500 dark:text-neutral-400">Created</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{formatDateTime(product.created_at)}</dd>
            </div>
            <div>
              <dt className="text-neutral-500 dark:text-neutral-400">Last Updated</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{formatDateTime(product.updated_at)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* ═══ Action Bar ═══ */}
      <div className="shrink-0 flex flex-wrap items-center gap-2 px-6 py-3 bg-white dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700">
        <span
          title={
            !product.is_active
              ? 'Product is inactive'
              : isOutOfStock(stockView)
                ? 'Product is out of stock'
                : undefined
          }
        >
          <Button
            variant="primary"
            size="sm"
            onClick={handleAddToCart}
            disabled={!product.is_active || isOutOfStock(stockView)}
          >
            <ShoppingCartIcon />
            <span className="ml-1.5">Add to Cart</span>
          </Button>
        </span>

        {canViewCost && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setStockHistoryPage(1);
              setShowStockHistory(true);
            }}
          >
            <ClockIcon />
            <span className="ml-1.5">Stock History</span>
          </Button>
        )}
      </div>

      {/* ═══ Variant Picker (same picker as the billing grid) ═══ */}
      {variantPicker && (
        <VariantMatrix
          isOpen={showVariantPicker}
          onClose={() => setShowVariantPicker(false)}
          productName={product.name}
          attributes={variantPicker.attributes}
          variants={variantPicker.variants}
          currency={currency}
          parentStock={Number(product.stock_quantity)}
          onSelect={(variant) => {
            const child = variantPicker.children.get(variant.id);
            if (!child) return;
            addItem(child);
            navigate('/pos/billing');
          }}
        />
      )}

      {/* ═══ Stock History Modal ═══ */}
      <Modal
        open={showStockHistory}
        onClose={() => setShowStockHistory(false)}
        title="Stock History"
        variant="wide"
      >
        <div className="space-y-4">
          {isLoadingHistory ? (
            <div className="space-y-2">
              <Skeleton variant="table-row" count={5} animated />
            </div>
          ) : stockHistory.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-8">
              No stock history available.
            </p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider border-b border-neutral-200 dark:border-neutral-700">
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Unit Cost</th>
                      <th className="px-3 py-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                    {stockHistory.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-3 py-2 text-neutral-600 dark:text-neutral-400">
                          {formatDateTime(entry.created_at)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="info" size="sm">{entry.type}</Badge>
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${
                          entry.quantity > 0
                            ? 'text-success-600 dark:text-success-400'
                            : 'text-danger-600 dark:text-danger-400'
                        }`}>
                          {entry.quantity > 0 ? '+' : ''}{entry.quantity}
                        </td>
                        <td className="px-3 py-2 text-right text-neutral-700 dark:text-neutral-300">
                          {entry.unit_cost ? formatCurrency(entry.unit_cost, currency) : '—'}
                        </td>
                        <td className="px-3 py-2 text-neutral-500 dark:text-neutral-400">
                          {entry.notes || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list */}
              <div className="md:hidden space-y-2">
                {stockHistory.map((entry) => (
                  <div
                    key={entry.id}
                    className="bg-neutral-50 dark:bg-neutral-900 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="info" size="sm">{entry.type}</Badge>
                      <span className={`font-medium text-sm ${
                        entry.quantity > 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-danger-600 dark:text-danger-400'
                      }`}>
                        {entry.quantity > 0 ? '+' : ''}{entry.quantity}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 space-y-0.5">
                      <div>{formatDateTime(entry.created_at)}</div>
                      {entry.unit_cost > 0 && <div>Unit cost: {formatCurrency(entry.unit_cost, currency)}</div>}
                      {entry.notes && <div>{entry.notes}</div>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {stockHistoryMeta.totalPages > 1 && (
                <Pagination
                  currentPage={stockHistoryMeta.page}
                  totalPages={stockHistoryMeta.totalPages}
                  totalItems={stockHistoryMeta.total}
                  pageSize={STOCK_HISTORY_PAGE_SIZE}
                  onPageChange={setStockHistoryPage}
                />
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
